// src/components/coordinateGrid/ShapeRenderer.tsx
/**
 * ShapeRenderer Component
 * 
 * Renders geometric shapes with optional fill and measurements.
 * Phase 3: Points and Basic Shapes
 */

import React, { useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../types/redux';
import { selectAllShapes, selectDrawingShape, selectShape } from '../../store/slices/gridShapesSlice';
import { GridShape, Point2D } from '../../types/coordinateGrid';
import { calculateCentroid, calculateDistance } from '../../lib/coordinateGrid/shapeCalculations';

interface ShapeRendererProps {
  /** Canvas dimensions */
  width: number;
  height: number;
  /** Viewport transform */
  scaleX: number;
  scaleY: number;
  offsetX: number;
  offsetY: number;
  /** Dark mode */
  isDark?: boolean;
}

/**
 * Convert math coordinates to screen coordinates
 */
function mathToScreen(
  point: Point2D,
  scaleX: number,
  scaleY: number,
  offsetX: number,
  offsetY: number
): { x: number; y: number } {
  return {
    x: offsetX + point.x * scaleX,
    y: offsetY - point.y * scaleY, // Y is inverted in screen coordinates
  };
}

/**
 * Generate vertex labels (A, B, C, ...)
 */
function getVertexLabel(index: number): string {
  return String.fromCharCode(65 + index); // A, B, C, ...
}

/**
 * Get midpoint of an edge for measurement label placement
 */
function getEdgeMidpoint(p1: Point2D, p2: Point2D): Point2D {
  return {
    x: (p1.x + p2.x) / 2,
    y: (p1.y + p2.y) / 2,
  };
}

export const ShapeRenderer: React.FC<ShapeRendererProps> = ({
  width,
  height,
  scaleX,
  scaleY,
  offsetX,
  offsetY,
  isDark = false,
}) => {
  const dispatch = useDispatch();
  const shapes = useSelector(selectAllShapes);
  const drawingShape = useSelector(selectDrawingShape);

  // Handle shape click
  const handleShapeClick = useCallback((id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    dispatch(selectShape({ id, ctrlKey: e.ctrlKey || e.metaKey }));
  }, [dispatch]);

  // Render shapes
  const shapeElements = useMemo(() => {
    return shapes.map((shape) => {
      if (shape.vertices.length < 2) {
        return null;
      }

      // Convert vertices to screen coordinates
      const screenVertices = shape.vertices.map((v) =>
        mathToScreen(v, scaleX, scaleY, offsetX, offsetY)
      );

      // Create polygon path
      const pathData =
        screenVertices.length > 0
          ? `M ${screenVertices[0].x} ${screenVertices[0].y} ${screenVertices
              .slice(1)
              .map((v) => `L ${v.x} ${v.y}`)
              .join(' ')} Z`
          : '';

      const textColor = isDark ? '#E5E7EB' : '#1F2937';
      const strokeColor = shape.isSelected ? '#F59E0B' : shape.color;
      const strokeWidth = shape.isSelected ? 3 : 2;

      // Calculate centroid for area label
      const centroid = calculateCentroid(shape.vertices);
      const centroidScreen = mathToScreen(centroid, scaleX, scaleY, offsetX, offsetY);

      return (
        <g
          key={shape.id}
          className={`shape-${shape.id}`}
          onClick={(e) => handleShapeClick(shape.id, e)}
          style={{ cursor: 'pointer' }}
        >
          {/* Shape fill */}
          {shape.fillColor && (
            <path
              d={pathData}
              fill={shape.fillColor}
              fillOpacity={shape.fillOpacity}
              stroke="none"
              className="shape-fill"
            />
          )}

          {/* Shape outline */}
          <path
            d={pathData}
            fill="none"
            stroke={strokeColor}
            strokeWidth={strokeWidth}
            strokeLinejoin="round"
            strokeLinecap="round"
            className="shape-outline"
          />

          {/* Vertex labels */}
          {shape.showVertexLabels &&
            shape.vertices.map((vertex, index) => {
              const screen = mathToScreen(vertex, scaleX, scaleY, offsetX, offsetY);
              return (
                <text
                  key={`vertex-${index}`}
                  x={screen.x + 6}
                  y={screen.y - 6}
                  fill={textColor}
                  fontSize="12"
                  fontWeight="600"
                  className="shape-vertex-label"
                >
                  {getVertexLabel(index)}
                </text>
              );
            })}

          {/* Vertex coordinates */}
          {shape.showCoordinates &&
            shape.vertices.map((vertex, index) => {
              const screen = mathToScreen(vertex, scaleX, scaleY, offsetX, offsetY);
              return (
                <text
                  key={`coord-${index}`}
                  x={screen.x + 6}
                  y={screen.y + 18}
                  fill={textColor}
                  fontSize="10"
                  className="shape-vertex-coordinates"
                >
                  ({vertex.x.toFixed(1)}, {vertex.y.toFixed(1)})
                </text>
              );
            })}

          {/* Side length measurements */}
          {shape.showMeasurements &&
            shape.vertices.map((vertex, index) => {
              const nextIndex = (index + 1) % shape.vertices.length;
              const nextVertex = shape.vertices[nextIndex];
              const distance = calculateDistance(vertex, nextVertex);
              const edgeMidpoint = getEdgeMidpoint(vertex, nextVertex);
              const midScreen = mathToScreen(edgeMidpoint, scaleX, scaleY, offsetX, offsetY);

              return (
                <text
                  key={`measure-${index}`}
                  x={midScreen.x}
                  y={midScreen.y - 8}
                  fill={textColor}
                  fontSize="10"
                  textAnchor="middle"
                  className="shape-measurement"
                >
                  {distance.toFixed(2)}
                </text>
              );
            })}

          {/* Area display */}
          {shape.showArea && shape.calculatedArea !== undefined && (
            <text
              x={centroidScreen.x}
              y={centroidScreen.y}
              fill={textColor}
              fontSize="12"
              fontWeight="600"
              textAnchor="middle"
              className="shape-area-label"
            >
              Area: {shape.calculatedArea.toFixed(2)}
            </text>
          )}
        </g>
      );
    });
  }, [shapes, width, height, scaleX, scaleY, offsetX, offsetY, isDark, handleShapeClick]);

  // Render drawing shape (preview)
  const drawingShapeElement = useMemo(() => {
    if (!drawingShape || !drawingShape.isDrawing || drawingShape.vertices.length < 2) {
      return null;
    }

    const screenVertices = drawingShape.vertices.map((v) =>
      mathToScreen(v, scaleX, scaleY, offsetX, offsetY)
    );

    // Create path for drawing preview
    const pathData = `M ${screenVertices[0].x} ${screenVertices[0].y} ${screenVertices
      .slice(1)
      .map((v) => `L ${v.x} ${v.y}`)
      .join(' ')}`;

    return (
      <g className="drawing-shape-preview">
        <path
          d={pathData}
          fill="none"
          stroke="#3B82F6"
          strokeWidth={2}
          strokeDasharray="4 4"
          strokeLinejoin="round"
          strokeLinecap="round"
        />
        {screenVertices.map((v, i) => (
          <circle
            key={`draw-vertex-${i}`}
            cx={v.x}
            cy={v.y}
            r={4}
            fill="#3B82F6"
            stroke="white"
            strokeWidth={1}
          />
        ))}
      </g>
    );
  }, [drawingShape, scaleX, scaleY, offsetX, offsetY]);

  return (
    <g className="shapes-layer">
      {shapeElements}
      {drawingShapeElement}
    </g>
  );
};



